import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLazySelectOptions } from "../hooks/useLazySelectOptions";
import {
  useCreateCustomerMutation,
  useLazyGetCustomerByIdQuery,
  useLazyGetCustomerStatusQuery,
  useUpdateCustomerMutation,
} from "../store/api";
import { extractList } from "../utils/response";
import { mergeLookupOption } from "../components/Leads/leadFormHelpers";
import {
  getErrorMessage,
  getValidationErrorDetails,
} from "../utils/errorHandler";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  Avatar,
  Container,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PhoneIcon from "@mui/icons-material/Phone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CUSTOMERS_LIST_PATH } from "../navigations/path";
import { useAuth } from "../hooks/useAuth";

const CUSTOMER_ROLE = "Customer";

const genderOptions = [
  { value: "", label: "Not Specified" },
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "O", label: "Other" },
];

const capitalizeWords = (text) => {
  if (!text) return "";
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
};

const generateSecurePassword = (length = 12) => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
  let password = "";
  const ensureChars = [
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)],
    "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)],
    "0123456789"[Math.floor(Math.random() * 10)],
    "!@#$%^&*()_+~`|}{[]:;?><,./-="[Math.floor(Math.random() * 20)],
  ];

  const array = new Uint8Array(length - ensureChars.length);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  } else {
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }

  for (let i = 0; i < array.length; i++) {
    password += chars[array[i] % chars.length];
  }

  return (ensureChars.join("") + password)
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("");
};

const formatDobForInput = (value) => {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const buildCustomerFormData = (formData, { isEdit, avatarFile }) => {
  const fd = new FormData();
  if (isEdit) fd.append("_method", "PUT");

  fd.append("name", formData.name ?? "");
  fd.append("email", formData.email ?? "");
  fd.append("username", formData.username ?? "");
  fd.append("phone", formData.phone ?? "");
  fd.append("gender", formData.gender ?? "");
  fd.append("dob", formData.dob ?? "");
  fd.append("role", CUSTOMER_ROLE);
  fd.append("status_id", formData.status ?? "");

  if (formData.password?.trim()) {
    fd.append("password", formData.password);
    fd.append("password_confirmation", formData.password);
  }

  if (avatarFile) {
    fd.append("avatar_id", avatarFile);
  }

  return fd;
};

const CreateCustomer = () => {
  const [createCustomer] = useCreateCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [fetchCustomerById] = useLazyGetCustomerByIdQuery();
  const [fetchCustomerStatus] = useLazyGetCustomerStatusQuery();
  const { hasPermission } = useAuth();

  const statusSelect = useLazySelectOptions(fetchCustomerStatus, {
    mapResult: (res) =>
      Array.isArray(res) ? res : (res?.list ?? extractList(res)),
  });
  const statuses = statusSelect.options;

  const canViewCustomer = hasPermission("customer-view");
  const canEditCustomer = hasPermission("customer-edit");

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get("edit_id");
  const viewId = searchParams.get("view");
  const isEditMode = !!editId;
  const isViewMode = !!viewId;
  const customerId = editId || viewId;

  const initialFormData = {
    name: "",
    username: "",
    email: "",
    password: isEditMode || isViewMode ? "" : generateSecurePassword(),
    phone: "",
    gender: "",
    dob: "",
    status: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!customerId);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!customerId) return;
      if (!canEditCustomer && !canViewCustomer) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const customerData = await fetchCustomerById(customerId).unwrap();
        const customer = customerData?.data ?? customerData;
        const statusId =
          customer?.status &&
          typeof customer.status === "object" &&
          customer.status.id
            ? customer.status.id
            : (customer?.status_id ?? customer?.status ?? "");
        const statusEntity =
          customer?.status && typeof customer.status === "object"
            ? customer.status
            : statusId
              ? { id: statusId, name: String(statusId) }
              : null;

        if (statusEntity?.id) {
          statusSelect.setOptions(mergeLookupOption([], statusEntity));
        }

        const existingAvatar =
          customer?.avatar?.path ||
          customer?.avatar?.url ||
          customer?.avatar_url ||
          "";

        setFormData({
          name: customer?.name ?? "",
          username: customer?.username ?? "",
          email: customer?.email ?? "",
          password: "",
          phone: customer?.phone ?? "",
          gender: customer?.gender ?? "",
          dob: formatDobForInput(customer?.dob),
          status: statusId,
        });
        setAvatarPreview(existingAvatar || "");
        setAvatarFile(null);
      } catch (error) {
        console.error("Fetch customer error:", error);
        toast.error(
          error?.data?.message ||
            getErrorMessage(error) ||
            "Failed to load customer data.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [customerId, canEditCustomer, canViewCustomer, fetchCustomerById]);

  useEffect(() => {
    if (!avatarFile) return undefined;
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  const validateForm = (data) => {
    const validationErrors = {};
    if (!data.name.trim()) validationErrors.name = "Full Name is required.";
    if (!data.username.trim()) {
      validationErrors.username = "Username is required.";
    } else if (!/^[a-zA-Z0-9_]{3,15}$/.test(data.username)) {
      validationErrors.username =
        "Username must be 3-15 alphanumeric characters or underscores.";
    }

    if (!data.email.trim()) validationErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      validationErrors.email = "Invalid email format.";
    }

    if (!isEditMode && !data.password.trim()) {
      validationErrors.password =
        "Initial Password is required for new customers.";
    }
    if (!data.status) validationErrors.status = "Status is required.";

    return validationErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "phone") {
      const digits = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: digits }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword();
    setFormData((prev) => ({ ...prev, password: newPassword }));
    setErrors((prev) => ({ ...prev, password: "" }));
  };

  const handleCopyPassword = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(formData.password)
        .then(() => setSnackbarOpen(true))
        .catch(() => toast.error("Failed to copy password."));
    } else {
      const tempInput = document.createElement("input");
      tempInput.value = formData.password;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
  };

  const clearAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationData =
      isEditMode && !formData.password.trim()
        ? { ...formData, password: "DUMMY_PASSWORD" }
        : formData;

    const validationErrors = validateForm(validationData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length) {
      toast.error("Please fix the errors in the form.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildCustomerFormData(formData, {
        isEdit: isEditMode,
        avatarFile,
      });

      if (isEditMode) {
        await updateCustomer({ id: customerId, data: payload }).unwrap();
        toast.success(`Customer ${formData.name} updated successfully!`);
      } else {
        await createCustomer(payload).unwrap();
        toast.success(`Customer ${formData.name} added successfully!`);
      }

      navigate(CUSTOMERS_LIST_PATH);
    } catch (error) {
      const { message, errors: serverErrors } =
        getValidationErrorDetails(error);
      toast.error(
        message ||
          (isEditMode
            ? "Failed to update customer."
            : "Failed to add customer. Please check the data."),
      );
      if (Object.keys(serverErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...serverErrors }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const formTitle = isViewMode
    ? `View Customer: ${formData.name}`
    : isEditMode
      ? `Edit Customer: ${formData.name}`
      : "Add Customer";
  const submitButtonText = isEditMode ? "Update Customer" : "Add Customer";
  const isFormDisabled = isViewMode || isSubmitting;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          // bgcolor: '#fff',
          minHeight: "100vh",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {(isViewMode || isEditMode) && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(CUSTOMERS_LIST_PATH)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
        )}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: "bold", color: "primary.main", mb: 4 }}
        >
          {formTitle}
        </Typography>

        <Paper
          elevation={4}
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            maxWidth: "900px",
            mx: "auto",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{
              mb: 3,
              borderBottom: "2px solid #eee",
              pb: 1,
              color: "primary.main",
            }}
          >
            Customer Details
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={isFormDisabled}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={isFormDisabled}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AlternateEmailIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={isFormDisabled}
                  error={!!errors.username}
                  helperText={
                    errors.username ||
                    "Must be unique and 3-15 alphanumeric characters."
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={genderOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    genderOptions.find((g) => g.value === formData.gender) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "gender",
                        value: newValue ? newValue.value : "",
                      },
                    });
                  }}
                  disabled={isFormDisabled}
                  renderInput={(params) => (
                    <TextField {...params} label="Gender" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={isFormDisabled}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={
                    isEditMode
                      ? "New Password (Leave blank to keep old)"
                      : "Initial Password"
                  }
                  name="password"
                  type="text"
                  value={formData.password}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={isFormDisabled}
                  error={!!errors.password}
                  helperText={
                    errors.password ||
                    (isEditMode
                      ? "Leave blank to keep current password."
                      : "Securely generated default password.")
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          onClick={handleGeneratePassword}
                          startIcon={<VisibilityOffIcon />}
                          color="primary"
                          size="small"
                          sx={{ mr: 1, whiteSpace: "nowrap" }}
                          disabled={isFormDisabled}
                        >
                          Generate
                        </Button>
                        <IconButton
                          onClick={handleCopyPassword}
                          edge="end"
                          aria-label="copy password"
                          color="primary"
                          size="small"
                          disabled={isFormDisabled}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={isFormDisabled}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="primary" />
                      </InputAdornment>
                    ),
                    inputProps: { maxLength: 15 },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statuses}
                  getOptionLabel={(option) =>
                    capitalizeWords(option.name || "")
                  }
                  value={statuses.find((s) => s.id === formData.status) || null}
                  onOpen={() => {
                    statusSelect.load().catch((error) => {
                      console.error("Fetch statuses error:", error);
                      toast.error(
                        error?.data?.message ||
                          getErrorMessage(error) ||
                          "Failed to load statuses.",
                      );
                    });
                  }}
                  loading={statusSelect.loading}
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "status",
                        value: newValue ? newValue.id : "",
                      },
                    });
                  }}
                  disabled={isFormDisabled}
                  noOptionsText="No statuses available"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer Status"
                      error={!!errors.status}
                      helperText={errors.status}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Role"
                  value={CUSTOMER_ROLE}
                  variant="outlined"
                  disabled
                  helperText="Fixed role for customer accounts"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Avatar
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {!isViewMode && (
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={isFormDisabled}
                    >
                      Upload avatar
                      <input
                        ref={fileInputRef}
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarPick}
                      />
                    </Button>
                  )}
                  {avatarPreview && (
                    <>
                      <Avatar
                        src={avatarPreview}
                        variant="rounded"
                        sx={{ width: 64, height: 64 }}
                      />
                      {!isViewMode && avatarFile && (
                        <Button
                          type="button"
                          size="small"
                          onClick={clearAvatar}
                        >
                          Remove new file
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Grid>

              {!isViewMode && (
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={isSubmitting ? null : <PersonAddIcon />}
                      disabled={isSubmitting}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        boxShadow: 6,
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 10,
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        submitButtonText
                      )}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </form>
        </Paper>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            sx={{ width: "100%", borderRadius: 1 }}
          >
            Password copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default CreateCustomer;
